import { useQuery } from '@tanstack/react-query';

// Mock API functions to simulate data fetching
const fetchFeaturedCourses = async () => {
  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 500));
  
  return [
    {
      id: '1',
      title: 'Advanced Project Management',
      description: 'Master agile, scrum, and lean methodologies.',
      icon: 'briefcase-outline',
      color: '#0e4e96',
      rating: 4.8,
      reviews: 1200,
      hasVideo: true,
      hasInteractive: true,
      isCertificateAvailable: true,
    },
    {
      id: '2',
      title: 'Data Analysis Fundamentals',
      description: 'Learn to analyze data using Excel and SQL.',
      icon: 'bar-chart-outline',
      color: '#19abb5',
      rating: 4.5,
      reviews: 850,
      hasVideo: true,
      hasInteractive: false,
      isCertificateAvailable: true,
    },
    {
      id: '3',
      title: 'Effective Communication',
      description: 'Improve your public speaking and writing skills.',
      icon: 'people-outline',
      color: '#fdb913',
      rating: 4.9,
      reviews: 2100,
      hasVideo: false,
      hasInteractive: true,
      isCertificateAvailable: false,
    },
    {
      id: '4',
      title: 'Modern React Native Development',
      description: 'Build cross-platform apps with the latest hooks and libraries.',
      icon: 'code-slash-outline',
      color: '#2c3e50',
      rating: 4.7,
      reviews: 1500,
      hasVideo: true,
      hasInteractive: true,
      isCertificateAvailable: true,
    },
    {
      id: '5',
      title: 'AutoCAD 2D & 3D Mastery',
      description: 'Essential course for Civil and Mechanical Engineering students.',
      icon: 'cube-outline',
      color: '#FF8C00',
      rating: 4.9,
      reviews: 3200,
      hasVideo: true,
      hasInteractive: true,
      isCertificateAvailable: true,
    },
    {
      id: '6',
      title: 'Structural Analysis with SAP2000',
      description: 'Advanced techniques for structural modeling and design.',
      icon: 'layers-outline',
      color: '#008080',
      rating: 4.6,
      reviews: 1800,
      hasVideo: true,
      hasInteractive: false,
      isCertificateAvailable: true,
    },
  ];
};

const fetchSkillCategories = async () => {
  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 300));
  
  return [
    {
      id: '1',
      title: 'Technical Skills',
      icon: 'laptop-outline',
      color: '#0e4e96',
    },
    {
      id: '2',
      title: 'Soft Skills',
      icon: 'people-outline',
      color: '#19abb5',
    },
    {
      id: '3',
      title: 'Leadership',
      icon: 'ribbon-outline',
      color: '#fdb913',
    },
    {
      id: '4',
      title: 'Design & Creative',
      icon: 'color-palette-outline',
      color: '#2c3e50',
    },
    {
      id: '5',
      title: 'AI & Machine Learning',
      icon: 'sparkles-outline',
      color: '#FF5733',
    },
    {
      id: '6',
      title: 'Financial Literacy',
      icon: 'wallet-outline',
      color: '#33FF57',
    },
    {
      id: '7',
      title: 'Engineering & CAD',
      icon: 'construct-outline',
      color: '#FF8C00', // Orange for Engineering
    },
  ];
};

/**
 * AI API Integration: Analyze skill gap based on user's current skills and target job.
 * 
 * NOTE: This function currently uses a MOCK response.
 * To switch to a real AI API (e.g., OpenAI, Gemini), you must:
 * 1. Implement a secure backend endpoint to handle the AI call (to hide your API key).
 * 2. Replace the mock logic below with a 'fetch' call to your new backend endpoint.
 * This simulates a call to an external AI service (e.g., OpenAI, Gemini)
 */
const analyzeSkillGapAPI = async (currentSkills, targetJob) => {
  // Simulate a longer network delay for AI processing
  // *** START OF MOCK LOGIC ***
  await new Promise(resolve => setTimeout(resolve, 2000));
  
  // In a real app, you would call your backend which then calls the AI API
  // Example: fetch('/api/ai/skill-gap', { method: 'POST', body: JSON.stringify({ skills: currentSkills, job: targetJob }) })
  
  // Mocked response from the AI API
  const mockApiResponse = {
    success: true,
    skillGap: [
      'Advanced Project Management (Scrum/Agile)',
      'Team Leadership & Mentoring',
      'Financial Budgeting & Forecasting',
      'Six Sigma Methodologies',
      'Data Visualization with Tableau',
    ],
    recommendedCourses: ['1', '3'], // IDs of recommended courses
  };
  
  // *** END OF MOCK LOGIC ***
  
  if (mockApiResponse.success) {
    return mockApiResponse;
  } else {
    throw new Error('AI analysis failed.');
  }
};

// --- React Query Hooks ---

export const useFeaturedCourses = () => {
  return useQuery({
    queryKey: ['featuredCourses'],
    queryFn: fetchFeaturedCourses,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useSkillCategories = () => {
  return useQuery({
    queryKey: ['skillCategories'],
    queryFn: fetchSkillCategories,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
};

export const useSkillGapAnalysis = (currentSkills, targetJob) => {
  const isEnabled = !!currentSkills && !!targetJob;
  
  return useQuery({
    queryKey: ['skillGapAnalysis', currentSkills, targetJob],
    queryFn: () => analyzeSkillGapAPI(currentSkills, targetJob),
    enabled: isEnabled,
    retry: 1,
  });
};


// --- Internship Matching API Mock ---

/**
 * @typedef {object} Internship
 * @property {string} id
 * @property {string} title
 * @property {string} company
 * @property {string} location
 * @property {string} matchScore
 * @property {string} icon
 */

/**
 * Mock API call to match user skills (from resume) to internship opportunities.
 * In a real app, this would be an AI API call to a backend endpoint.
 * @param {string[]} userSkills - Skills extracted from the user's resume.
 * @returns {Promise<Internship[]>}
 */
const fetchInternshipMatches = async (userSkills) => {
  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 500));

  // Mock AI matching logic:
  const isEngineering = userSkills.includes("AutoCAD 2D & 3D Mastery") || userSkills.includes("Structural Analysis with SAP2000");
  const isTech = userSkills.includes("Modern React Native Development") || userSkills.includes("Data Analysis Fundamentals");

  let opportunities = [
    {
      id: 'I1',
      title: 'Software Development Intern',
      company: 'Tech Solutions Inc.',
      location: 'Remote',
      matchScore: '95%',
      icon: 'code-slash-outline',
    },
    {
      id: 'I2',
      title: 'Data Analyst Intern',
      company: 'Global Data Corp',
      location: 'New York',
      matchScore: '88%',
      icon: 'bar-chart-outline',
    },
    {
      id: 'I3',
      title: 'Marketing Intern',
      company: 'Creative Agency',
      location: 'Dubai',
      matchScore: '65%',
      icon: 'megaphone-outline',
    },
  ];

  if (isEngineering) {
    opportunities.unshift({
      id: 'I4',
      title: 'Civil Engineering Intern',
      company: 'Structural Builders',
      location: 'Riyadh',
      matchScore: '98%',
      icon: 'construct-outline',
    });
  }

  return opportunities;
};

export const useInternshipMatches = (userSkills) => {
  return useQuery({
    queryKey: ['internshipMatches', userSkills],
    queryFn: () => fetchInternshipMatches(userSkills),
    enabled: !!userSkills && userSkills.length > 0,
  });
};


// --- Personalized Learning Paths API Mock ---

/**
 * @typedef {object} LearningPath
 * @property {string} id
 * @property {string} title
 * @property {string} description
 * @property {string} duration
 * @property {string[]} courseIds
 * @property {string} icon
 */

/**
 * Mock API call to generate personalized learning paths based on user goals.
 * @param {string} userGoal - e.g., 'Promotion to Senior', 'Graduation Requirement', 'Career Change'.
 * @returns {Promise<LearningPath[]>}
 */
const fetchLearningPaths = async (userGoal) => {
  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 500));

  const paths = [
    {
      id: 'LP1',
      title: 'Senior Software Engineer Path',
      description: 'Master advanced architecture, leadership, and system design.',
      duration: '6 Months',
      courseIds: ['4', '1'],
      icon: 'rocket-outline',
    },
    {
      id: 'LP2',
      title: 'Civil Engineering Project Manager',
      description: 'Focus on structural analysis, project management, and team leadership.',
      duration: '4 Months',
      courseIds: ['5', '6', '1'],
      icon: 'construct-outline',
    },
    {
      id: 'LP3',
      title: 'Data Science Career Track',
      description: 'From fundamentals to advanced machine learning techniques.',
      duration: '8 Months',
      courseIds: ['2', '3'],
      icon: 'analytics-outline',
    },
  ];

  // Simple mock personalization
  if (userGoal.includes('Engineering')) {
    return paths.filter(p => p.title.includes('Engineering'));
  }

  return paths;
};

export const useLearningPaths = (userGoal) => {
  return useQuery({
    queryKey: ['learningPaths', userGoal],
    queryFn: () => fetchLearningPaths(userGoal),
    enabled: !!userGoal,
  });
};


// ===== NEW: Mock Student Certificates Data =====
const fetchStudentCertificates = async () => {
  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 600));
  
  return [
    {
      id: 'CERT-001',
      name: 'React Advanced Development',
      earnedDate: 'October 15, 2025',
      skillsAcquired: ['React Hooks', 'State Management', 'API Integration', 'Performance Optimization'],
      proficiencyLevel: 'Advanced',
      verificationLink: 'https://jobeng.com/verify/CERT-001',
    },
    {
      id: 'CERT-002',
      name: 'JavaScript Mastery',
      earnedDate: 'September 20, 2025',
      skillsAcquired: ['ES6+', 'Async Programming', 'DOM Manipulation', 'Testing'],
      proficiencyLevel: 'Advanced',
      verificationLink: 'https://jobeng.com/verify/CERT-002',
    },
    {
      id: 'CERT-003',
      name: 'Node.js Backend Development',
      earnedDate: 'August 10, 2025',
      skillsAcquired: ['Express.js', 'Database Design', 'API Development', 'Authentication'],
      proficiencyLevel: 'Intermediate',
      verificationLink: 'https://jobeng.com/verify/CERT-003',
    },
  ];
};

// ===== NEW: React Query Hook for Student Certificates =====
export const useStudentCertificates = () => {
  return useQuery({
    queryKey: ['studentCertificates'],
    queryFn: fetchStudentCertificates,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
